import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ username, password });
      toast.success('Giriş başarılı');
      const redirect = searchParams.get('redirect');
      if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
        navigate(redirect);
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-primary-900">
      {/* Animated decorative blobs */}
      <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="blob-1 absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary-400/40 blur-3xl"></div>
        <div className="blob-2 absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-cyan-400/30 blur-3xl"></div>
        <div className="blob-3 absolute top-1/2 left-1/3 w-72 h-72 rounded-full bg-sky-300/20 blur-3xl"></div>
      </div>

      {/* Subtle grid overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      ></div>

      {/* Glass card */}
      <div className="relative w-full max-w-md">
        {/* Glow ring */}
        <div className="absolute -inset-0.5 rounded-3xl bg-gradient-to-br from-white/40 via-white/10 to-white/30 blur-sm"></div>

        <div className="relative bg-white/15 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/30 p-8 sm:p-10">
          {/* Logo + başlık */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-44 h-44 sm:w-52 sm:h-52 rounded-3xl bg-white/95 backdrop-blur shadow-2xl mb-5 p-5">
              <img
                src="/mim_logo.svg"
                alt="MİM"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden w-full h-full items-center justify-center">
                <WrenchScrewdriverIcon className="w-24 h-24 text-primary-700" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white drop-shadow-sm">Bakım-Onarım</h1>
            <p className="text-white/80 mt-1 text-sm">Cihaz Takip Sistemi</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Kullanıcı */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Kullanıcı Adı
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/20 focus:border-white/50 transition backdrop-blur"
                  placeholder="Kullanıcı adı"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            {/* Şifre */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">
                Şifre
              </label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/20 focus:border-white/50 transition backdrop-blur"
                  placeholder="Şifre"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition p-1"
                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 bg-white text-primary-700 font-semibold py-3.5 rounded-xl shadow-lg hover:bg-primary-50 hover:shadow-xl active:scale-[0.99] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-primary-700" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Giriş yapılıyor...
                </>
              ) : (
                <>
                  Giriş Yap
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Kart içi alt yazı */}
          <p className="text-center text-xs text-white/60 mt-8">
            bakim.mimbytes.tr
          </p>
        </div>
      </div>

      {/* Sayfa altı: Powered by */}
      <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
        <p className="text-xs sm:text-sm text-white/70 drop-shadow">
          Powered by Mim Bytes © Tüm hakları saklıdır {new Date().getFullYear()}
        </p>
      </div>

      {/* Blob animation styles */}
      <style>{`
        @keyframes blob-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33%      { transform: translate(40px, -30px) scale(1.08); }
          66%      { transform: translate(-30px, 40px) scale(0.95); }
        }
        .blob-1 { animation: blob-float 18s ease-in-out infinite; }
        .blob-2 { animation: blob-float 22s ease-in-out infinite reverse; }
        .blob-3 { animation: blob-float 26s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default Login;
